/* fourpaw-standard: lifecycle-delegated */
(() => {
  if (window.SafeWalkV8GiftPackLifecycle) return;
  const KACHING = '__kaching_bundles';
  const config = () => window.SafeWalkV8GiftPackRegistry || {};
  const propertiesFor = (item) => item?.properties && typeof item.properties === 'object'
    ? item.properties
    : {};
  const metadataFor = (item) => {
    const raw = propertiesFor(item)[KACHING];
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    const candidates = [String(raw)];
    try { candidates.push(window.atob(String(raw))); } catch (_) { /* JSON is the normal format */ }
    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch (_) { /* try the next supported encoding */ }
    }
    return null;
  };
  const productId = (item) => Number(item?.product_id || item?.product?.id || 0);
  const variantId = (item) => Number(item?.variant_id || item?.id || 0);
  const lineTotal = (item) => Number.isFinite(Number(item?.final_line_price))
    ? Number(item.final_line_price)
    : Number(item?.final_price || 0) * Number(item?.quantity || 1);
  const originalTotal = (item) => Number.isFinite(Number(item?.original_line_price))
    ? Number(item.original_line_price)
    : Number(item?.original_price || 0) * Number(item?.quantity || 1);
  const parentKey = (item) => String(
    item?.parent_relationship?.parent_key
      || item?.parent_relationship?.parent?.key
      || ''
  );
  const kindFor = (metadata, registry = config()) => {
    if (!metadata || metadata.main !== true || metadata.deal !== registry.deal) return '';
    if (metadata.bid === registry.bars?.harness_only) return 'harness_only';
    if (metadata.bar === registry.bars?.harness_bungee) return 'harness_bungee';
    if (metadata.bar === registry.bars?.full_set) return 'full_set';
    return '';
  };
  const linkedTo = (gift, main) => {
    const explicitParent = parentKey(gift);
    if (explicitParent) return explicitParent === String(main?.key || '');
    return String(metadataFor(gift)?.id || '') === String(metadataFor(main)?.id || '');
  };
  const classify = (cart, registry = config()) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const mains = items.filter((item) => (
      productId(item) === Number(registry.qualifyingProductId)
      && Boolean(kindFor(metadataFor(item), registry))
    ));
    const packLines = items.filter((item) => productId(item) === Number(registry.giftProductId));
    const consumed = new Set();
    const groups = mains.map((main) => {
      const metadata = metadataFor(main);
      const kind = kindFor(metadata, registry);
      const expectedGiftId = registry.giftIds?.[kind] || '';
      const gifts = packLines.filter((gift) => {
        const giftMetadata = metadataFor(gift);
        return linkedTo(gift, main)
          && giftMetadata?.deal === registry.deal
          && String(giftMetadata?.id || '') === String(metadata?.id || '')
          && giftMetadata?.gift === expectedGiftId;
      });
      gifts.forEach((gift) => consumed.add(gift.key));
      const mainQuantity = Number(main.quantity || 0);
      const giftQuantity = gifts.reduce((sum, gift) => sum + Number(gift.quantity || 0), 0);
      const complete = Boolean(expectedGiftId)
        && mainQuantity > 0
        && lineTotal(main) > 0
        && gifts.length === 1
        && giftQuantity === mainQuantity
        && gifts.every((gift) => lineTotal(gift) === 0);
      return { main, kind, gifts, mainQuantity, giftQuantity, complete };
    });
    const orphans = packLines.filter((gift) => !consumed.has(gift.key));
    return {
      groups,
      orphans,
      harnessQuantity: groups.reduce((sum, group) => sum + group.mainQuantity, 0),
      giftQuantity: groups.reduce((sum, group) => sum + group.giftQuantity, 0),
      complete: groups.length === 0
        ? orphans.length === 0
        : groups.every((group) => group.complete) && orphans.length === 0
    };
  };
  let request = null;
  let timer = 0;
  let observer = null;
  const checkoutSelector = 'a[href*="/checkout"], button[name="checkout"], [data-checkout-button]';
  const setCheckoutGuard = (blocked) => {
    document.documentElement.classList.toggle('safewalk-gift-pack-invalid', blocked);
    document.querySelectorAll(checkoutSelector).forEach((node) => {
      if (blocked) {
        node.dataset.safewalkGiftPackBlocked = 'true';
        node.setAttribute('aria-disabled', 'true');
      } else if (node.dataset.safewalkGiftPackBlocked === 'true') {
        delete node.dataset.safewalkGiftPackBlocked;
        node.removeAttribute('aria-disabled');
      }
    });
  };
  const syncGiftSurfaceVisibility = (state) => {
    const active = new Set(state?.groups?.map((group) => String(group.main?.key || '')) || []);
    const hide = Number(state?.harnessQuantity || 0) <= 0;
    document.querySelectorAll('[data-safewalk-gift-pack-group]').forEach((group) => {
      const mainKey = group.dataset.safewalkGiftPackMainKey;
      group.hidden = hide || Boolean(mainKey && !active.has(mainKey));
    });
    document.querySelectorAll('[data-safewalk-gift-pack-section]').forEach((section) => {
      section.hidden = hide;
    });
    document.querySelectorAll('.cart-drawer-gift-container--real').forEach((container) => {
      if (container.querySelector('[data-safewalk-gift-pack-group]')) container.hidden = hide;
    });
  };
  const lockBundleQuantities = () => {
    const products = ['safewalk-no-pull-2', 'bungee-leash', 'padded-collar'];
    products.forEach((handle) => {
      document.querySelectorAll(`cart-drawer .cart-item--product-${handle}:has(dl) quantity-input`).forEach((control) => {
        control.querySelectorAll('button').forEach((button) => {
          button.hidden = true;
          button.disabled = true;
          button.setAttribute('aria-hidden', 'true');
          button.style.setProperty('display', 'none', 'important');
        });
        const input = control.querySelector('input');
        if (input) input.readOnly = true;
      });
    });
  };
  const renderSections = (body) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.renderContents && body?.sections?.['cart-drawer']) {
      drawer.renderContents(body);
      return;
    }
    const html = body?.sections?.['cart-drawer'];
    if (!html || !drawer) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('cart-drawer');
    if (fresh) drawer.innerHTML = fresh.innerHTML;
  };
  const readCart = async () => {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`cart-${response.status}`);
    return response.json();
  };
  const sectionIds = () => {
    const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
    document.querySelector('cart-drawer')?.getSectionsToRender?.()
      .forEach((section) => ids.add(section.id));
    return [...ids].filter(Boolean).slice(0, 5);
  };
  const postCart = async (url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    });
    if (!response.ok) throw new Error(`cart-write-${response.status}`);
    return response.json();
  };
  const withMetadata = (item, metadata) => ({
    ...propertiesFor(item),
    [KACHING]: JSON.stringify(metadata)
  });
  const withoutMetadata = (item, detachedGroup = '') => {
    const properties = { ...propertiesFor(item) };
    delete properties[KACHING];
    if (detachedGroup) properties._safewalk_v8_detached_from_group = detachedGroup;
    return properties;
  };
  const targetMainMetadata = (metadata, kind, registry) => {
    const next = { ...metadata, main: true };
    if (kind === 'harness_only') {
      next.bid = registry.bars?.harness_only;
      delete next.bar;
    } else {
      next.bar = registry.bars?.[kind];
      delete next.bid;
    }
    return next;
  };
  const targetChildMetadata = (metadata, changes = {}) => {
    const next = { ...metadata, ...changes };
    delete next.bid;
    if (!changes.bundleProduct) delete next.bundleProduct;
    if (!changes.bar) delete next.bar;
    return next;
  };
  const cartEntry = (item, properties, parentId = 0) => ({
    id: variantId(item),
    quantity: Number(item.quantity || 1),
    ...(parentId ? { parent_id: parentId } : {}),
    properties
  });
  const planAccessoryRemoval = (cart, lineIndex, registry = config()) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const clicked = items[Number(lineIndex) - 1];
    const clickedMetadata = metadataFor(clicked);
    if (!clicked || clickedMetadata?.deal !== registry.deal
      || !clickedMetadata?.id || !clickedMetadata?.bundleProduct) return null;
    const groupId = String(clickedMetadata.id);
    const group = items.filter((item) => {
      const metadata = metadataFor(item);
      return metadata?.deal === registry.deal && String(metadata?.id || '') === groupId;
    });
    const main = group.find((item) => metadataFor(item)?.main === true);
    const mainMetadata = metadataFor(main);
    const kind = kindFor(mainMetadata, registry);
    const gift = group.find((item) => productId(item) === Number(registry.giftProductId));
    if (!main || !gift || !['harness_bungee', 'full_set'].includes(kind)) return null;
    const mainId = variantId(main);
    const giftMetadata = metadataFor(gift);
    const p1 = () => [
      cartEntry(main, withMetadata(main, targetMainMetadata(mainMetadata, 'harness_only', registry))),
      cartEntry(gift, withMetadata(gift, targetChildMetadata(giftMetadata, {
        gift: registry.giftIds?.harness_only
      })), mainId)
    ];
    let target = [];
    if (kind === 'harness_bungee') {
      target = p1();
    } else if (clickedMetadata.bundleProduct === registry.bundleIds?.full_set?.collar) {
      const bungee = group.find((item) => (
        metadataFor(item)?.bundleProduct === registry.bundleIds?.full_set?.bungee
      ));
      if (!bungee) return null;
      target = [
        cartEntry(main, withMetadata(main, targetMainMetadata(mainMetadata, 'harness_bungee', registry))),
        cartEntry(bungee, withMetadata(bungee, targetChildMetadata(metadataFor(bungee), {
          bar: registry.bars?.harness_bungee,
          bundleProduct: registry.bundleIds?.harness_bungee?.bungee
        })), mainId),
        cartEntry(gift, withMetadata(gift, targetChildMetadata(giftMetadata, {
          gift: registry.giftIds?.harness_bungee
        })), mainId)
      ];
    } else if (clickedMetadata.bundleProduct === registry.bundleIds?.full_set?.bungee) {
      const collar = group.find((item) => (
        metadataFor(item)?.bundleProduct === registry.bundleIds?.full_set?.collar
      ));
      target = p1();
      if (collar) target.push(cartEntry(collar, withoutMetadata(collar, groupId)));
    } else {
      return null;
    }
    const original = group.map((item) => cartEntry(
      item,
      { ...propertiesFor(item) },
      item === main ? 0 : mainId
    ));
    return { clicked, group, groupId, original, target };
  };
  const removePlanLines = async (plan, includeDetached = false) => {
    const cart = includeDetached ? await readCart() : null;
    const lines = includeDetached ? cart.items.filter((item) => {
      const metadata = metadataFor(item);
      return (metadata?.deal === config().deal && String(metadata?.id || '') === plan.groupId)
        || propertiesFor(item)._safewalk_v8_detached_from_group === plan.groupId;
    }) : plan.group;
    const updates = Object.fromEntries(lines.map((item) => [item.key, 0]).filter(([key]) => key));
    if (Object.keys(updates).length) await postCart('/cart/update.js', { updates });
  };
  let transition = null;
  const transitionAccessoryRemoval = (lineIndex, expectedKey = '') => {
    if (transition) return transition;
    transition = (async () => {
      setCheckoutGuard(true);
      const cart = await readCart();
      const plan = planAccessoryRemoval(cart, lineIndex);
      if (!plan || (expectedKey && plan.clicked.key !== expectedKey)) {
        const body = await postCart('/cart/change.js', { line: lineIndex, quantity: 0 });
        renderSections(body);
        return reconcileExistingCart();
      }
      await removePlanLines(plan);
      try {
        const body = await postCart('/cart/add.js', { items: plan.target });
        renderSections(body);
        const verified = await readCart();
        const state = classify(verified);
        if (!state.complete) throw new Error('gift-pack-transition-verify');
        setCheckoutGuard(false);
        document.dispatchEvent(new CustomEvent('safewalk:gift-pack-repaired', {
          detail: { transition: true, removedAccessory: variantId(plan.clicked) }
        }));
        return state;
      } catch (error) {
        await removePlanLines(plan, true);
        const restored = await postCart('/cart/add.js', { items: plan.original });
        renderSections(restored);
        throw error;
      }
    })().catch((error) => {
      setCheckoutGuard(true);
      return { complete: false, error: error instanceof Error ? error.message : 'transition' };
    }).finally(() => { transition = null; });
    return transition;
  };
  const removeOrphans = async (orphans, plannedUpdates = {}) => {
    const updates = { ...plannedUpdates }; orphans.forEach((item) => { if (item.key) updates[item.key] = 0; });
    if (!Object.keys(updates).length) return null;
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    });
    if (!response.ok) throw new Error(`cart-repair-${response.status}`);
    const body = await response.json();
    renderSections(body);
    document.dispatchEvent(new CustomEvent('safewalk:gift-pack-repaired', { detail: { removed: orphans.length, updated: Object.keys(plannedUpdates).length } }));
    return body;
  };
  const reconcileExistingCart = async () => {
    if (request) return request;
    request = (async () => {
      const cart = await readCart();
      let state = classify(cart); if (!state.complete) setCheckoutGuard(true);
      const duplicatePlan = window.SafeWalkV8GiftPackRepair?.plan(state);
      if (duplicatePlan?.updates) { const repaired = await removeOrphans([], duplicatePlan.updates); if (repaired) state = classify(repaired); }
      if (state.orphans.length) {
        const repaired = await removeOrphans(state.orphans); if (repaired) state = classify(repaired);
      }
      syncGiftSurfaceVisibility(state);
      lockBundleQuantities();
      setCheckoutGuard(!state.complete);
      document.dispatchEvent(new CustomEvent('safewalk:gift-pack-contract', { detail: state }));
      return state;
    })().catch((error) => {
      setCheckoutGuard(true);
      return { complete: false, error: error instanceof Error ? error.message : 'unknown' };
    }).finally(() => { request = null; });
    return request;
  };
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(reconcileExistingCart, 80);
  };
  const reconcileAfterRemoval = () => [200, 800, 1800].forEach((delay) => window.setTimeout(reconcileExistingCart, delay));
  const mutationTouchesCart = (record) => Boolean(record.target?.closest?.('cart-drawer, cart-items, #main-cart-items')) || [...record.addedNodes, ...record.removedNodes]
    .some((node) => node.nodeType === 1 && (
      node.matches?.('cart-drawer, cart-items, #main-cart-items, [data-safewalk-gift-pack-group]')
      || node.querySelector?.('cart-drawer, cart-items, #main-cart-items, [data-safewalk-gift-pack-group]')
    ));
  const onClick = (event) => {
    const retry = event.target.closest?.('[data-safewalk-gift-pack-retry]');
    if (retry) { event.preventDefault(); reconcileExistingCart(); return; }
    const remove = event.target.closest?.('cart-drawer cart-remove-button[data-index]');
    const row = remove?.closest?.('.cart-item');
    if (remove && row?.querySelector?.('.cart-drawer-item__quantity-and-prices--has-props')
      && (row.classList.contains('cart-item--product-bungee-leash')
        || row.classList.contains('cart-item--product-padded-collar'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      transitionAccessoryRemoval(Number(remove.dataset.index || 0)).catch(() => {});
      return;
    }
    if (remove) reconcileAfterRemoval();
    const checkout = event.target.closest?.(checkoutSelector);
    if (checkout?.dataset.safewalkGiftPackBlocked === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelector('[data-safewalk-gift-contract="invalid"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  };
  document.addEventListener('click', onClick, true);
  observer = new MutationObserver((records) => {
    if (records.some(mutationTouchesCart)) schedule();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  ['pageshow', 'shopify:section:load'].forEach((name) => window.addEventListener(name, schedule));
  setCheckoutGuard(Boolean(document.querySelector('[data-safewalk-gift-contract="invalid"]')));
  schedule();
  window.SafeWalkV8GiftPackLifecycle = Object.freeze({
    classify, kindFor, metadataFor, planAccessoryRemoval,
    reconcileExistingCart, schedule, transitionAccessoryRemoval
  });
})();
